import { useEffect, useState, useCallback, useRef } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { DayPicker } from 'react-day-picker';
import es from 'date-fns/locale/es';
import 'react-day-picker/dist/style.css';

moment.locale(navigator.userLanguage || navigator.language);

const DateDropdownStyled = styled.div`
	display: inline-block;

	.dropdown {
		.btn {
			color: ${props => props.color ?? 'inherit'};
		}

		.dropdown-menu {
			padding: 0;
			border-color: transparent;
			box-shadow: 0px 0px 10px rgba(1,1,1,0.2);
			z-index: 9999;

			&.start {
				left: 0;
			}

			&.end {
				right: 0;
			}

			@media (max-width: 768px) {
				position: fixed;
				top: 5%;
				left: 50% !important; 
				width: 295px !important;
				margin-left: -147.5px !important;
				max-height: 90%;
				overflow: auto;
			}
		}
	}
`;

const DropdownMenuInner = styled.div`
	display: flex;

	@media (max-width: 768px) {
		flex-direction: column;
	}
`;

const DropdownOptions = styled.div`
	background: var(--bs-gray-100);
`;

const DayPickerWrapper = styled.div`
	padding: 5px;
	.rdp {
		margin: 0;
		--rdp-accent-color: var(--bs-primary);

		.selected {
			background: var(--bs-primary);
		}
	}
`;

const ToggleButton = styled.button`
	&:hover {
		color: var(--plenoil-black) !important;
	}
`

export const periods = {
	total: 		'Todo el periodo',
	yesterday: 	'Ayer',
	today: 		'Hoy',
	tomorrow: 	'Mañana',
	thisweek: 	'Esta semana',
	lastweek: 	'Semana pasada',
	thismonth: 	'Este mes',
	lastmonth: 	'Mes pasado',
	thisyear: 	'Este año',
	lastyear: 	'Año pasado',
	custom: 	'Rango personalizado'
};


export const getDatesRangeFromPeriod = (period, onlyDates = false) => {
	// Prepare dates
	let date_from = null;
	let date_to = null;

	switch(period) {
		case 'today': 
			date_from = moment().format('YYYY-MM-DD');
			date_to = moment().format('YYYY-MM-DD');
		break;
		case 'yesterday': 
			date_from = moment().subtract(1, 'days').format('YYYY-MM-DD');
			date_to = moment().subtract(1, 'days').format('YYYY-MM-DD');
		break;
		case 'tomorrow': 
			date_from = moment().add(1, 'days').format('YYYY-MM-DD');
			date_to = moment().add(1, 'days').format('YYYY-MM-DD');
		break;
		case 'thisweek': 
			date_from = moment().startOf('weeks').format('YYYY-MM-DD');
			date_to = moment().endOf('weeks').format('YYYY-MM-DD');
		break;
		case 'lastweek': 
			date_from = moment().subtract(1, 'weeks').startOf('weeks').format('YYYY-MM-DD');
			date_to = moment().subtract(1, 'weeks').endOf('weeks').format('YYYY-MM-DD');
		break;
		case 'thismonth': 
			date_from = moment().startOf('month').format('YYYY-MM-DD');
			date_to = moment().endOf('month').format('YYYY-MM-DD');
		break;
		case 'lastmonth': 
			date_from = moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD');
			date_to = moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD');
		break;
		case 'thisyear': 
			date_from = moment().startOf('year').format('YYYY-MM-DD');
			date_to = moment().endOf('year').format('YYYY-MM-DD');
		break;
		case 'lastyear': 
			date_from = moment().subtract(1, 'years').startOf('year').format('YYYY-MM-DD');
			date_to = moment().subtract(1, 'years').endOf('year').format('YYYY-MM-DD');
		break;
		default: // Total
			date_from = '2021-01-01';
			date_to = '3000-01-01';
	}

	if ( period.indexOf('custom_') !== -1 ) {
		let split = period.split('_');
		date_from = split[1];
		date_to = split[2];
	}

	if ( !onlyDates ) {
		date_from += ' 00:00:00';
		date_to += ' 23:59:59';
	}
	
	return {
		from: date_from,
		to: date_to
	};
}

export default function DateDropdown(props) {
	const element = useRef(null);

	const [opened, _setOpened] = useState(false);
	const openedRef = useRef(opened);
	const setOpened = (status) => {
		openedRef.current = status;
		_setOpened(status);
	}

	const [period, setPeriod] = useState(props.defaultPeriod ?? 'today');

	const [range, _setRange] = useState(props.startRange ? props.startRange : null);
	const rangeRef = useRef(range);
	const setRange = (rng) => {
		rangeRef.current = rng;
		_setRange(rng);
	}

	let id = props.id ?? null;
	let mode = props.mode ?? 'range';
	let showPeriods = props.showPeriods ?? true;

	const setPeriodClick = useCallback((e, period) => {
		if (e) e.preventDefault();

		let datesRangeFromPeriod = getDatesRangeFromPeriod(period, props.onlyDates);
		setPeriod(period.indexOf('custom_') !== -1 ? 'custom' : period);
		setOpened(false);
		if ( period.indexOf('custom_') === -1 ) setRange(null);

		if ( props.onChange ) props.onChange(period, datesRangeFromPeriod);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const accept = () => {
		if ( !rangeRef?.current?.from ) return;
		setPeriodClick(null, 'custom_' + moment(rangeRef.current.from).format('YYYY-MM-DD') + '_' + moment(rangeRef.current.to ?? rangeRef.current.from).format('YYYY-MM-DD'));
	}

	const toggleDropdown = useCallback((forceStatus = null) => {
		if ( forceStatus !== null ) {
			// Fix to leave the last value if click outside
			if ( forceStatus === false && openedRef.current ) {
				accept();
			} 

			setOpened(forceStatus);
			return;
		}

		setOpened(opened ? false : true);
	}, []);

	useEffect(() => {
		if ( props.setPeriod === undefined ) return;
		setPeriod(null);
		setRange(null);
		setPeriodClick(null, props.setPeriod);
	}, [props.setPeriod, setPeriodClick]);

	useEffect(() => {
		let clickEventListener = (e) => {
			if ( element?.current === e.target || element?.current?.contains(e.target) ) {
				return;
			}
			toggleDropdown(false);
		};

		document.addEventListener('click', clickEventListener);

		return function cleanup() {
			document.removeEventListener('click', clickEventListener);
		}
	}, []);

	useEffect(() => {
        const handleKeyDown = (event) => {
			event.stopPropagation();

            if (event.key === 'Escape' && opened) {
				event.preventDefault();

                toggleDropdown(false);
            }
        };

        element.current.addEventListener('keydown', handleKeyDown);

        return () => {
            element.current?.removeEventListener('keydown', handleKeyDown);
        };
    }, [opened, toggleDropdown]);

	useEffect(() => {
		let dropdownMenuElement = element.current.querySelector('.dropdown-menu');
		let position = dropdownMenuElement.getBoundingClientRect();
		if ( position.left < 0 ) {
			dropdownMenuElement.style.left = 0;
			dropdownMenuElement.style.right = 'auto';
		} else {
			dropdownMenuElement.style.left = 'auto';
			dropdownMenuElement.style.right = 0;
		}
	}, [opened]);

	// Prepare period rows
	let periodsElements = [];
	for(let i in periods) {
		if ( i === 'custom' ) continue;

		let title = periods[i];
		periodsElements.push(<li key={i}><a className="dropdown-item" href="." onClick={(e) => setPeriodClick(e, i)}>{title}</a></li>)
	}

	let custom_selected = <span></span>;
	if (period === 'custom' && range && range.from) {
		if (mode === 'single') custom_selected = moment(range.from).format('DD-MM-YYYY');
		if (mode === 'range') {
			let fromFormatted = moment(range.from).format('DD-MM-YYYY');
			let toFormatted = moment(range.to ?? range.from).format('DD-MM-YYYY');

			if ( fromFormatted === toFormatted ) custom_selected = fromFormatted;
			else custom_selected = fromFormatted + ' al ' + toFormatted;
		}
	}

	return (
		<DateDropdownStyled id={id} className="date-dropdown h-100" color={props.color} ref={element}>
			<div className="dropdown d-inline-block ms-auto h-100">
				<ToggleButton className="btn btn-sm h-100" type="button" onClick={() => toggleDropdown()}>
					 { period && period !== 'custom' ? periods[period] : custom_selected } <small><i className="bi bi-caret-down-fill"></i></small>
				</ToggleButton>
				<div className={'dropdown-menu ' + (opened ? 'show ' : '') + (props.alignment ?? 'end ')} onClick={(e) => e.stopPropagation()}>
					<DropdownMenuInner>
						{showPeriods &&
							<DropdownOptions>
								{ periodsElements }
							</DropdownOptions>
						}
						<DayPickerWrapper>
							<DayPicker 
								locale={es}
								mode={mode}
								selected={range}
								onSelect={(rng) => {
									// Convert single to day to range
									if (mode === 'single') {
										rng = { from: rng, to: rng };
									}
								
									// setPeriod(null); // Needed because if exists global selector sending dates to this selector, we need to reset period to avoid weird things
									setRange(rng);
								}}
							/>
							<div className="text-center">
								<button 
									className="btn btn-sm btn-primary text-white" 
									style={{width: '150px'}}
									onClick={(e) => accept()}
									disabled={!range?.from}
								>
									Aceptar
								</button>	
							</div>
						</DayPickerWrapper>
					</DropdownMenuInner>
				</div>
			</div>
		</DateDropdownStyled>
	);
}